import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi, Spinner, ErrBanner, today, fmtDate, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import FuelBalance from '../FuelBalance/FuelBalance';
import './Dip.css';

const SUB_TABS = [
  { id: 'DipReading',  labelKey: 'dip.tabs.dipReading' },
  { id: 'DipChart',    labelKey: 'dip.tabs.dipChart' },
  { id: 'FuelBalance', labelKey: 'dip.tabs.fuelBalancing' },
];

const EMPTY = { reading_date: today(), tank_id:'', tank_label:'', opening_dip:'', closing_dip:'', opening_qty:'', closing_qty:'' };

export default function Dip({ subPage = 'DipReading' }) {
  const { t: tr } = useTranslation();
  const [tab, setTab] = useState(subPage);
  useEffect(() => { setTab(subPage); setShowForm(false); }, [subPage]);

  const { data: tanks }    = useApi(() => api.tanks.list(), []);
  const { data: readings, loading, error, reload } = useApi(() => api.dipReadings.all(), []);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm]    = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]      = useState('');
  const notify = (m) => { setMsg(m); setTimeout(()=>setMsg(''), 3500); };

  const handleTankSelect = (e) => {
    const t = (tanks||[]).find(x => String(x.id) === e.target.value);
    setForm(f => ({
      ...f,
      tank_id:     e.target.value,
      tank_label:  t ? `${t.tank_no} (${t.product_code})` : f.tank_label,
      opening_qty: t ? String(t.current_stock) : '',
    }));
  };

  const handleSave = async () => {
    if (!form.opening_dip || !form.closing_dip) { notify('❌ ' + tr('dip.errors.dipRequired')); return; }
    if (!form.tank_id) { notify('❌ ' + tr('dip.errors.tankRequired')); return; }
    setSaving(true);
    try {
      await api.dipReadings.create({
        reading_date: form.reading_date,
        tank_id:      parseInt(form.tank_id),
        tank_label:   form.tank_label,
        opening_dip:  parseFloat(form.opening_dip),
        closing_dip:  parseFloat(form.closing_dip),
        opening_qty:  parseFloat(form.opening_qty)||0,
        closing_qty:  parseFloat(form.closing_qty)||0,
      });
      notify('✅ ' + tr('dip.toasts.saved')); setShowForm(false); setForm(EMPTY); reload();
    } catch (e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(tr('dip.confirmDelete'))) return;
    try { await api.dipReadings.remove(id); notify('✅ ' + tr('dip.toasts.deleted')); reload(); }
    catch (e) { notify('❌ ' + e.message); }
  };

  const previewSold = () => fmtNum(Math.max(0, (parseFloat(form.opening_qty)||0) - (parseFloat(form.closing_qty)||0)));

  // Chart: group by tank_label, show sold_qty per day
  const chartData = useMemo(() => {
    const grouped = {};
    (readings||[]).forEach(r => {
      if (!grouped[r.tank_label]) grouped[r.tank_label] = [];
      grouped[r.tank_label].push({ date: r.reading_date, sold: Number(r.sold_qty||0), closing: Number(r.closing_qty||0) });
    });
    return grouped;
  }, [readings]);

  const tankVisuals = (tanks||[]).map(t => {
    const pct  = Math.round((Number(t.current_stock)/Number(t.capacity))*100)||0;
    const color = t.product_code==='MS'?'#e53e3e':t.product_code==='HSD'?'#1a6fad':'#2d9e5f';
    return { ...t, pct, color };
  });

  return (
    <div className="dip-page">
      <div className="page-header">
        <h2 className="page-title">{tr('dip.title')}</h2>
        <div style={{display:'flex', gap:8}}>
          {tab === 'DipReading' && (
            <button className="btn btn-primary btn-sm" onClick={()=>setShowForm(!showForm)}>＋ {tr('dip.recordDip')}</button>
          )}
          <button className="export-btn" onClick={()=>window.print()}>🖨️ {tr('common.print')}</button>
        </div>
      </div>

      <div className="sub-tab-bar">
        {SUB_TABS.map(t => (
          <button key={t.id} className={`sub-tab ${tab === t.id ? 'active' : ''}`}
            onClick={() => { 
              setTab(t.id); 
              setShowForm(false); 
              window.location.hash = t.id; // Sync URL
            }}>
            {tr(t.labelKey)}
          </button>
        ))}
      </div>

      {msg && <div className="notify-bar">{msg}</div>}

      {/* ── TANK VISUAL GAUGES (both tabs) ── */}
      <div className="dip-tanks">
        {tankVisuals.map(t => (
          <div key={t.id} className="dip-tank-visual">
            <div className="dip-tank-label">{t.tank_no} {t.product_code}</div>
            <div className="dip-tank-container">
              <div className="dip-tank-fill" style={{height:`${t.pct}%`, background:t.color}}/>
              <div className="dip-level-line" style={{bottom:`${t.pct}%`}}>
                <span>{t.pct}%</span>
              </div>
            </div>
            <div className="dip-tank-qty">
              {fmtNum(t.current_stock)} / {fmtNum(t.capacity)} L
            </div>
            <div style={{fontSize:11,color:Number(t.current_stock)<=Number(t.min_level)?'var(--danger)':'var(--secondary)',fontWeight:700,marginTop:2}}>
              {Number(t.current_stock)<=Number(t.min_level)?tr('dip.lowStock'):tr('dip.ok')}
            </div>
          </div>
        ))}
        {tankVisuals.length === 0 && (
          <div style={{padding:20, color:'var(--text-muted)', fontSize:13}}>
            {tr('dip.noTanks')}
          </div>
        )}
      </div>

      {tab === 'FuelBalance' && <FuelBalance />}

      {/* ── DIP READING TAB ── */}
      {tab === 'DipReading' && (
        <>
          {showForm && (
            <div className="section-card">
              <div className="section-header">{tr('dip.recordDipReading')}</div>
              <div className="form-row">
                <div className="form-group"><label>{tr('common.date')}</label>
                  <input type="date" className="form-control" value={form.reading_date}
                    onChange={e=>setForm({...form,reading_date:e.target.value})}/>
                </div>
                <div className="form-group"><label>{tr('common.tank')}</label>
                  <select className="form-control" value={form.tank_id} onChange={handleTankSelect}>
                    <option value="">{tr('dip.selectTank')}</option>
                    {(tanks||[]).map(t=><option key={t.id} value={t.id}>{t.tank_no} ({t.product_code})</option>)}
                  </select>
                </div>
                <div className="form-group"><label>{tr('dip.openingDipCm')}</label>
                  <input type="number" className="form-control" value={form.opening_dip}
                    onChange={e=>setForm({...form,opening_dip:e.target.value})} step="0.01" placeholder="0.00"/>
                </div>
                <div className="form-group"><label>{tr('dip.closingDipCm')}</label>
                  <input type="number" className="form-control" value={form.closing_dip}
                    onChange={e=>setForm({...form,closing_dip:e.target.value})} step="0.01" placeholder="0.00"/>
                </div>
                <div className="form-group"><label>{tr('dip.openingQtyL')}</label>
                  <input type="number" className="form-control" value={form.opening_qty}
                    onChange={e=>setForm({...form,opening_qty:e.target.value})} step="0.001" placeholder="0.000"/>
                </div>
                <div className="form-group"><label>{tr('dip.closingQtyL')}</label>
                  <input type="number" className="form-control" value={form.closing_qty}
                    onChange={e=>setForm({...form,closing_qty:e.target.value})} step="0.001" placeholder="0.000"/>
                </div>
                {(form.opening_qty || form.closing_qty) && (
                  <div className="form-group balance-group">
                    <label>{tr('dip.soldQty')}</label>
                    <div className="balance-value" style={{color:'var(--secondary)',background:'#f0fff4',borderColor:'#9ae6b4'}}>
                      {previewSold()} L
                    </div>
                  </div>
                )}
              </div>
              <div className="action-bar" style={{padding:'0 16px 14px'}}>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving?tr('common.saving'):'💾 '+tr('common.save')}</button>
                <button className="btn btn-secondary" onClick={()=>{setShowForm(false);setForm(EMPTY);}}>✕ {tr('common.cancel')}</button>
              </div>
            </div>
          )}

          {loading && <Spinner />}
          {error   && <ErrBanner msg={error} />}

          {!loading && (
            <div className="section-card">
              <div className="section-header">{tr('dip.recordsCount', { n: (readings||[]).length })}</div>
              <div className="table-wrapper">
                <table className="data-table">
                  <thead><tr>
                    <th style={{textAlign:'left'}}>#</th>
                    <th style={{textAlign:'left'}}>{tr('common.date')}</th>
                    <th style={{textAlign:'left'}}>{tr('common.tank')}</th>
                    <th>{tr('dip.openingDipCm')}</th>
                    <th>{tr('dip.closingDipCm')}</th>
                    <th>{tr('dip.openingQtyL')}</th>
                    <th>{tr('dip.closingQtyL')}</th>
                    <th>{tr('dip.soldQtyL')}</th>
                    <th style={{textAlign:'left'}}>{tr('common.action')}</th>
                  </tr></thead>
                  <tbody>
                    {(readings||[]).map((r,i)=>(
                      <tr key={r.id}>
                        <td>{i+1}</td>
                        <td>{fmtDate(r.reading_date)}</td>
                        <td style={{fontWeight:600}}>{r.tank_label}</td>
                        <td className="right-align">{fmtNum(r.opening_dip)}</td>
                        <td className="right-align">{fmtNum(r.closing_dip)}</td>
                        <td className="right-align">{fmtNum(r.opening_qty)}</td>
                        <td className="right-align">{fmtNum(r.closing_qty)}</td>
                        <td className="right-align" style={{color:'var(--secondary)',fontWeight:700}}>
                          {fmtNum(r.sold_qty)}
                        </td>
                        <td><button className="btn btn-sm btn-danger" onClick={()=>handleDelete(r.id)}>{tr('dip.del')}</button></td>
                      </tr>
                    ))}
                    {(readings||[]).length===0 && (
                      <tr><td colSpan={9} style={{textAlign:'center',padding:20,color:'var(--text-muted)'}}>{tr('dip.noReadings')}</td></tr>
                    )}
                  </tbody>
                  {(readings||[]).length>0 && (
                    <tfoot>
                      <tr style={{background:'#e8f0f7',fontWeight:700}}>
                        <td colSpan={7} style={{padding:'8px 10px',textAlign:'right'}}>{tr('dip.totalSold')}</td>
                        <td className="right-align" style={{color:'var(--secondary)'}}>
                          {fmtNum((readings||[]).reduce((s,r)=>s+Number(r.sold_qty||0),0))} L
                        </td>
                        <td/>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── DIP CHART TAB ── */}
      {tab === 'DipChart' && (
        <div className="section-card">
          <div className="section-header">{tr('dip.chartTitle')}</div>
          {loading && <Spinner />}
          {!loading && Object.keys(chartData).length === 0 && (
            <div style={{padding:32,textAlign:'center',color:'var(--text-muted)'}}>
              {tr('dip.noReadingsChart')}
            </div>
          )}
          {!loading && Object.keys(chartData).map(tankLabel => {
            const rows = chartData[tankLabel];
            const maxSold = Math.max(...rows.map(r=>r.sold), 1);
            return (
              <div key={tankLabel} style={{padding:'16px'}}>
                <div style={{fontWeight:700,fontSize:14,color:'var(--primary-dark)',marginBottom:12}}>
                  ⛽ {tankLabel}
                </div>
                {/* Bar chart visual */}
                <div style={{display:'flex',alignItems:'flex-end',gap:8,height:120,marginBottom:8}}>
                  {rows.slice(-14).map((r,i)=>{
                    const h = Math.max(4, Math.round((r.sold/maxSold)*100));
                    return (
                      <div key={i} style={{display:'flex',flexDirection:'column',alignItems:'center',gap:4,flex:1}}>
                        <div style={{fontSize:9,color:'var(--text-muted)',fontFamily:'monospace'}}>{fmtNum(r.sold)}</div>
                        <div style={{width:'100%',background:'var(--primary)',borderRadius:'3px 3px 0 0',height:`${h}px`,minHeight:4,opacity:0.85}} title={`${r.date}: ${r.sold} L`}/>
                        <div style={{fontSize:9,color:'var(--text-muted)',transform:'rotate(-45deg)',whiteSpace:'nowrap'}}>
                          {r.date.slice(5)}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Closing stock table */}
                <div className="table-wrapper" style={{marginTop:8}}>
                  <table className="data-table">
                    <thead><tr>
                      <th style={{textAlign:'left'}}>{tr('common.date')}</th>
                      <th>{tr('dip.openingDipCm')}</th>
                      <th>{tr('dip.closingDipCm')}</th>
                      <th>{tr('dip.soldL')}</th>
                      <th>{tr('dip.closingStockL')}</th>
                    </tr></thead>
                    <tbody>
                      {(readings||[]).filter(r=>r.tank_label===tankLabel).map((r,i)=>(
                        <tr key={i}>
                          <td>{fmtDate(r.reading_date)}</td>
                          <td className="right-align">{fmtNum(r.opening_dip)}</td>
                          <td className="right-align">{fmtNum(r.closing_dip)}</td>
                          <td className="right-align" style={{color:'var(--secondary)',fontWeight:700}}>{fmtNum(r.sold_qty)}</td>
                          <td className="right-align">{fmtNum(r.closing_qty)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
